import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'

export interface AppSelectOption<TValue> {
  label: string
  value: TValue
}

export interface AppSelectGroup<TValue> {
  label: string
  options: readonly AppSelectOption<TValue>[]
}

export interface AppSelectProps<TValue> {
  id?: string
  options?: readonly AppSelectOption<TValue>[]
  // Options regroupées en sections (chaque section a un intitulé). Prioritaire sur `options` :
  // quand `groups` est fourni, le menu affiche des en-têtes de section (ex. « En préparation »).
  groups?: readonly AppSelectGroup<TValue>[]
  className?: string
  onChange?: (value: TValue | null) => void
  value?: TValue
  defaultValue?: TValue
  ariaInvalid?: boolean
  placeholder?: string
  size?: 'sm' | 'default' | 'xs' | '2xs'
  disabled?: boolean
}

export function AppSelect<TValue>({
  id,
  options = [],
  groups,
  onChange,
  className,
  value,
  defaultValue,
  ariaInvalid,
  placeholder,
  size = 'default',
  disabled = false,
}: AppSelectProps<TValue>) {
  // `items` alimente le typeahead et la résolution valeur→label de base-ui : il doit lister TOUTES
  // les options sélectionnables, sections confondues.
  const flatOptions = groups ? groups.flatMap((group) => group.options) : options
  const placeholderOption = placeholder ? [{ label: placeholder, value: null }] : []
  const items = [...placeholderOption, ...flatOptions]

  return (
    <Select
      id={id}
      items={items}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onValueChange={(value) => {
        onChange?.(value)
      }}
    >
      <SelectTrigger
        className={className}
        aria-invalid={ariaInvalid}
        size={size}
        disabled={disabled}
      >
        <SelectValue />
      </SelectTrigger>

      <SelectContent alignItemWithTrigger={false}>
        {placeholderOption.map((option) => (
          <SelectItem key={option.label} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {groups
          ? groups.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.options.map((option) => (
                  <SelectItem key={option.label} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          : options.map((option) => (
              <SelectItem key={option.label} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  )
}
